import React, { useState, useEffect } from 'react';
import './TwoFactorSetup.css';

const TwoFactorSetup = ({ currentUser, onBack, onUpdate }) => {
  const [step, setStep] = useState('initial'); // initial, setup, verify, enabled
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [password, setPassword] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7071/api';

  useEffect(() => {
    // Check if 2FA is already enabled
    if (currentUser?.totp_enabled) {
      setStep('enabled');
    }
  }, [currentUser]);

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/auth/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          email: currentUser.email
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to enable 2FA');
      }

      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep('setup');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!verificationCode || verificationCode.length !== 6) {
        throw new Error('Please enter a valid 6-digit code');
      }

      const response = await fetch(`${apiUrl}/auth/2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          token: verificationCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      // Update user in localStorage
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      
      setSuccess('[SUCCESS] Two-factor authentication enabled successfully!');
      setStep('enabled');
      
      if (onUpdate) {
        onUpdate(data.user);
      }

      setTimeout(() => {
        setVerificationCode('');
      }, 1000);
    } catch (err) {
      setError('[ERROR] ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!password) {
        throw new Error('Please enter your password to disable 2FA');
      }

      const response = await fetch(`${apiUrl}/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          password: password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disable 2FA');
      }

      // Update user in localStorage
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      
      setSuccess('[SUCCESS] Two-factor authentication disabled');
      setStep('initial');
      setPassword('');
      
      if (onUpdate) {
        onUpdate(data.user);
      }
    } catch (err) {
      setError('[ERROR] ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="two-factor-setup">
      <div className="setup-header">
        <button onClick={onBack} className="back-button">
          ← Back to Settings
        </button>
        <h2>Two-Factor Authentication</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {step === 'initial' && (
        <div className="setup-card">
          <div className="icon-large">🔐</div>
          <h3>Secure Your Account</h3>
          <p>
            Two-factor authentication adds an extra layer of security to your account.
            You'll need to enter a code from your authenticator app each time you sign in.
          </p>
          
          <div className="info-section">
            <h4>How it works:</h4>
            <ol>
              <li>Install an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.)</li>
              <li>Scan the QR code we'll provide</li>
              <li>Enter the 6-digit code from your app to verify</li>
              <li>Use the app to sign in from now on</li>
            </ol>
          </div>

          <button 
            onClick={handleEnable2FA} 
            disabled={loading}
            className="primary-button"
          >
            {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
          </button>
        </div>
      )}

      {step === 'setup' && (
        <div className="setup-card">
          <h3>Scan QR Code</h3>
          <p>Use your authenticator app to scan this QR code:</p>
          
          <div className="qr-code-container">
            {qrCode && <img src={qrCode} alt="QR Code" />}
          </div>

          <div className="secret-key">
            <p><strong>Or enter this key manually:</strong></p>
            <code>{secret}</code>
            <button 
              onClick={() => navigator.clipboard.writeText(secret)}
              className="copy-button"
            >
              📋 Copy
            </button>
          </div>

          <form onSubmit={handleVerifySetup}>
            <div className="form-group">
              <label>Enter the 6-digit code from your authenticator app:</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                className="verification-input"
                disabled={loading}
              />
            </div>

            <div className="button-group">
              <button 
                type="button"
                onClick={() => setStep('initial')}
                className="secondary-button"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="primary-button"
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        </div>
      )}

      {step === 'enabled' && (
        <div className="setup-card">
          <div className="icon-large success">[SUCCESS]</div>
          <h3>Two-Factor Authentication is Active</h3>
          <p>Your account is protected with two-factor authentication.</p>

          <div className="warning-section">
            <h4>[WARNING] Important:</h4>
            <ul>
              <li>Keep your authenticator app safe and backed up</li>
              <li>You'll need it every time you sign in</li>
              <li>If you lose access, contact support for account recovery</li>
            </ul>
          </div>

          <form onSubmit={handleDisable2FA}>
            <div className="form-group">
              <label>Enter your password to disable 2FA:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="password-input"
                disabled={loading}
              />
            </div>

            <button 
              type="submit"
              className="danger-button"
              disabled={loading || !password}
            >
              {loading ? 'Disabling...' : 'Disable Two-Factor Authentication'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
